/**
 * AvatarPlayer - Unified entry point for RTC avatar streaming.
 *
 * This class provides a unified API that shields applications from
 * differences between different RTC providers (LiveKit, Agora, etc.)
 * and integrates seamlessly with @spatialwalk/avatarkit for rendering.
 *
 * @packageDocumentation
 */

import type { RTCProvider } from './RTCProvider';
import type { RTCConnectionConfig, RTCPrepareConnectionConfig } from '../types';
import { AnimationHandler } from './AnimationHandler';
import type { AnimationTrackCallbacks } from './types';
import { configureLogger, logger, type LogLevel, initializeTelemetry, trackEvent, flushTelemetry } from '../utils';

// Import AvatarView type from avatarkit
import type { AvatarView } from '@spatialwalk/avatarkit';

/**
 * Options for configuring AvatarPlayer.
 */
export interface AvatarPlayerOptions {
  /**
   * Log level for SDK output.
   * - 'info': Show all logs (debug mode)
   * - 'warning': Show warnings and errors (default)
   * - 'error': Show only errors
   * - 'none': Disable all logs
   */
  logLevel?: LogLevel;

  /**
   * Enable jitter buffer for smoother animation playback.
   * When enabled, frames are buffered and rendered in sequence order at a steady 25fps,
   * absorbing network jitter and out-of-order delivery.
   * Default: true
   */
  enableJitterBuffer?: boolean;

  /**
   * Maximum delay (in ms) a frame can sit in the jitter buffer before being rendered.
   * Only used when enableJitterBuffer is true.
   * Default: 80 (2 frames at 25fps)
   */
  maxBufferDelayMs?: number;
}

/**
 * Unified Avatar Player.
 *
 * Main entry point for applications to interact with RTC avatar streaming.
 * Handles connection management, audio publishing, animation rendering,
 * and coordinates with avatarkit for display.
 *
 * @example
 * ```typescript
 * import { AvatarPlayer, LiveKitProvider } from '@spatialwalk/avatarkit-rtc';
 * import { AvatarView, Avatar } from '@spatialwalk/avatarkit';
 *
 * // Create avatar and view (from avatarkit)
 * const avatar = await Avatar.create(characterId);
 * const avatarView = new AvatarView(avatar, container);
 *
 * // Create player
 * const provider = new LiveKitProvider();
 * const player = new AvatarPlayer(provider, avatarView);
 *
 * // Connect
 * await player.connect({ url: 'wss://...', token: '...' });
 *
 * // Option 1: Use microphone (most common)
 * await player.startPublishing();
 * await player.stopPublishing();
 *
 * // Option 2: Use custom audio source
 * const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
 * await player.publishAudio(stream.getAudioTracks()[0]);
 * await player.unpublishAudio();
 * stream.getTracks().forEach(t => t.stop());
 *
 * // Disconnect
 * await player.disconnect();
 * ```
 */
export class AvatarPlayer {
  /** @internal */
  private provider: RTCProvider;

  /** @internal */
  private avatarView: AvatarView;

  /** @internal */
  private animationHandler: AnimationHandler;

  /** @internal */
  private _isConnected = false;

  /** @internal */
  private localAudioTrack: MediaStreamTrack | null = null;

  /** @internal */
  private microphoneStream: MediaStream | null = null;

  /** @internal */
  private eventHandlers: Map<string, Set<(...args: unknown[]) => void>> =
    new Map();

  /** @internal */
  private lastConnectionConfig: RTCConnectionConfig | null = null;

  /** @internal */
  private isReconnecting = false;

  /** @internal */
  private hasActiveAnimationSession = false;

  /** @internal */
  private isTrackingPrimed = false;

  /** @internal */
  private connectStartTime = 0;

  /** @internal */
  private sessionStartTime = 0;

  /** @internal */
  private stallCount = 0;

  /** @internal */
  private reconnectCount = 0;

  /** @internal */
  private conversationCount = 0;

  /** @internal */
  private providerName = '';

  /**
   * Create a new AvatarPlayer instance.
   * @param provider - RTC provider implementation (e.g., LiveKitProvider)
   * @param avatarView - AvatarView instance from @spatialwalk/avatarkit
   * @param options - Optional configuration for transitions
   */
  constructor(
    provider: RTCProvider,
    avatarView: AvatarView,
    options?: AvatarPlayerOptions,
  ) {
    this.provider = provider;
    this.avatarView = avatarView;
    this.providerName = provider.name;

    // Configure logging if specified
    if (options?.logLevel !== undefined) {
      configureLogger({ level: options.logLevel });
    }

    // Initialize telemetry (piggybacks on avatarkit's PostHog instance)
    initializeTelemetry({ sdkVersion: __RTC_SDK_VERSION__ });
    logger.info('AvatarPlayer', `SDK version: ${__RTC_SDK_VERSION__}`);

    // Create internal renderer adapter
    const renderer = this.createRendererAdapter();

    this.animationHandler = new AnimationHandler(renderer, {
      enableJitterBuffer: options?.enableJitterBuffer,
      maxBufferDelayMs: options?.maxBufferDelayMs,
      providerName: this.providerName,
      onStreamStalled: () => {
        // AnimationHandler already switches to idle state
        // Just notify external listeners, let them decide what to do (e.g., call reconnect())
        this.hasActiveAnimationSession = false;
        this.isTrackingPrimed = false;
        this.stallCount++;
        trackEvent('rtc_stream_stalled', 'warning', {
          provider: this.providerName,
          session_elapsed: this.sessionStartTime ? Date.now() - this.sessionStartTime : 0,
        });
        this.emit('stalled');
      },
    });

    // Setup provider event handlers
    this.setupProviderEvents();
  }

  /**
   * Check if currently connected to RTC server.
   */
  get isConnected(): boolean {
    return this._isConnected;
  }

  /**
   * Connect to RTC server.
   * @param config - Connection configuration (URL, token, room name, etc.)
   * @throws Error if already connected or connection fails
   */
  async connect(config: RTCConnectionConfig): Promise<void> {
    if (this._isConnected) {
      throw new Error('Already connected. Please disconnect first.');
    }

    // Save config for potential reconnection
    this.lastConnectionConfig = config;
    this.connectStartTime = performance.now();

    trackEvent('rtc_connect_start', 'info', {
      provider: this.providerName,
    });

    try {
      // Setup animation callbacks before connecting
      await this.setupAnimationCallbacks();
      await this.provider.connect(config);
      this._isConnected = true;
      this.sessionStartTime = Date.now();
      this.stallCount = 0;
      this.reconnectCount = 0;
      this.conversationCount = 0;

      trackEvent('rtc_connect_success', 'info', {
        provider: this.providerName,
        duration: Math.round(performance.now() - this.connectStartTime),
      });
    } catch (error) {
      trackEvent('rtc_connect_failed', 'error', {
        provider: this.providerName,
        reason: error instanceof Error ? error.message : String(error),
        duration: Math.round(performance.now() - this.connectStartTime),
      });
      throw error;
    }
  }

  /**
   * Pre-warm a future RTC connection when the provider supports it.
   * This is best-effort and does not change the connected state.
   */
  async prepareConnection(config: RTCPrepareConnectionConfig): Promise<void> {
    await this.provider.prepareConnection?.(config);
  }

  /**
   * Disconnect from RTC server.
   * Stops all tracks and cleans up resources.
   */
  async disconnect(): Promise<void> {
    if (!this._isConnected) {
      return;
    }

    // Stop microphone if active
    if (this.microphoneStream) {
      await this.stopPublishing();
    } else if (this.localAudioTrack) {
      // Stop custom audio track
      await this.unpublishAudio();
    }

    // Collect session summary before teardown
    const sessionSummary = this.animationHandler.getSessionSummary();

    await this.provider.disconnect();
    this.animationHandler.dispose();
    this._isConnected = false;

    const sessionDuration = this.sessionStartTime ? Date.now() - this.sessionStartTime : 0;

    trackEvent('rtc_session_summary', 'info', {
      provider: this.providerName,
      total_duration_ms: sessionDuration,
      total_frames: sessionSummary.totalFrames,
      total_lost: sessionSummary.totalLost,
      total_recovered: sessionSummary.totalRecovered,
      total_dropped: sessionSummary.totalDropped,
      avg_fps: sessionSummary.avgFps,
      stall_count: this.stallCount,
      reconnect_count: this.reconnectCount,
      conversation_count: this.conversationCount,
    });

    trackEvent('rtc_disconnected', 'info', {
      provider: this.providerName,
      session_duration: sessionDuration,
    });

    flushTelemetry();
  }

  /**
   * Publish an audio track to the RTC server.
   *
   * The audio source is controlled externally - you can pass any audio track:
   * - Microphone: `getUserMedia({ audio: true })`
   * - Browser audio: `audioElement.captureStream()`
   * - Web Audio API: `audioContext.createMediaStreamDestination().stream`
   * - Screen share audio: `getDisplayMedia({ audio: true })`
   *
   * @param track - MediaStreamTrack to publish (must be an audio track)
   * @throws Error if not connected or track is invalid
   *
   * @example
   * ```typescript
   * // Microphone
   * const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
   * await player.publishAudio(stream.getAudioTracks()[0]);
   *
   * // Browser audio element
   * const audioEl = document.querySelector('audio');
   * const stream = audioEl.captureStream();
   * await player.publishAudio(stream.getAudioTracks()[0]);
   * ```
   */
  async publishAudio(track: MediaStreamTrack): Promise<void> {
    if (!this._isConnected) {
      throw new Error('Not connected. Please call connect() first.');
    }

    if (track.kind !== 'audio') {
      throw new Error('Invalid track: expected audio track');
    }

    // Unpublish existing track if any
    if (this.localAudioTrack) {
      await this.unpublishAudio();
    }

    this.localAudioTrack = track;
    try {
      await this.provider.publishAudioTrack(track);
    } catch (error) {
      this.localAudioTrack = null;
      trackEvent('rtc_audio_publish_failed', 'error', {
        provider: this.providerName,
        description: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  /**
   * Stop publishing audio.
   * Note: This does NOT stop the track - the caller is responsible for managing the track lifecycle.
   */
  async unpublishAudio(): Promise<void> {
    if (!this.localAudioTrack) {
      return;
    }

    await this.provider.unpublishAudioTrack();
    this.localAudioTrack = null;
  }

  /**
   * Start publishing microphone audio.
   *
   * This requests microphone permission and publishes it to the RTC session.
   * For custom audio sources (e.g., audio files), use `publishAudio(track)` instead.
   *
   * @throws Error if not connected, permission denied, or no microphone found
   *
   * @example
   * ```typescript
   * await player.startPublishing();
   * // ... later
   * await player.stopPublishing();
   * ```
   */
  async startPublishing(): Promise<void> {
    if (!this._isConnected) {
      throw new Error('Not connected. Please call connect() first.');
    }

    if (this.microphoneStream) {
      return; // Already using microphone
    }

    // Request microphone permission
    this.microphoneStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    const track = this.microphoneStream.getAudioTracks()[0];

    await this.publishAudio(track);
  }

  /**
   * Stop publishing microphone audio and release the microphone.
   *
   * This stops the microphone track and releases the device.
   * If you used `publishAudio()` with a custom track, use `unpublishAudio()` instead.
   */
  async stopPublishing(): Promise<void> {
    if (!this.microphoneStream) {
      return;
    }

    await this.unpublishAudio();

    // Stop and release microphone
    this.microphoneStream.getTracks().forEach((track) => track.stop());
    this.microphoneStream = null;
  }

  /**
   * Get current connection state.
   * @returns Connection state string (e.g., 'connected', 'disconnected')
   */
  getConnectionState(): string {
    return this.provider.getConnectionState();
  }

  /**
   * Get the native RTC client object.
   *
   * Returns the underlying RTC client for advanced use cases:
   * - LiveKit: Returns the Room instance
   * - Agora: Returns the IAgoraRTCClient instance
   *
   * @returns The native RTC client object, or null if not connected
   *
   * @example
   * ```typescript
   * // Access LiveKit Room
   * const room = player.getNativeClient() as Room;
   * console.log('Participants:', room?.remoteParticipants.size);
   *
   * // Access Agora Client
   * const client = player.getNativeClient() as IAgoraRTCClient;
   * console.log('State:', client?.connectionState);
   * ```
   */
  getNativeClient(): unknown {
    return this.provider.getNativeClient();
  }

  /**
   * Add event listener.
   * @param event - Event name ('connected', 'disconnected', 'error', 'stalled', etc.)
   * @param handler - Event handler function
   */
  on(event: string, handler: (...args: unknown[]) => void): void {
    if (event === 'stalled') {
      // Handle stalled event locally
      if (!this.eventHandlers.has(event)) {
        this.eventHandlers.set(event, new Set());
      }
      this.eventHandlers.get(event)!.add(handler);
    } else {
      // Forward other events to provider
      this.provider.on(event, handler);
    }
  }

  /**
   * Remove event listener.
   * @param event - Event name
   * @param handler - Event handler function (must be same reference as added)
   */
  off(event: string, handler: (...args: unknown[]) => void): void {
    if (event === 'stalled') {
      this.eventHandlers.get(event)?.delete(handler);
    } else {
      this.provider.off(event, handler);
    }
  }

  /**
   * Emit an event to local listeners.
   * @internal
   */
  private emit(event: string, ...args: unknown[]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(...args);
        } catch (e) {
          logger.error('AvatarPlayer', `Error in ${event} handler:`, e);
        }
      });
    }
  }

  /**
   * Reconnect to RTC server using the last connection configuration.
   * Useful for recovering from connection issues or stream stalls.
   *
   * @example
   * ```typescript
   * player.on('stalled', async () => {
   *   console.log('Stream stalled, attempting reconnection...');
   *   await player.reconnect();
   * });
   * ```
   *
   * @throws Error if no previous connection config exists or reconnection fails
   */
  async reconnect(): Promise<void> {
    if (this.isReconnecting) {
      logger.warn('AvatarPlayer', 'Already attempting reconnection, skipping');
      return;
    }

    if (!this.lastConnectionConfig) {
      throw new Error(
        'Cannot reconnect: no previous connection. Call connect() first.',
      );
    }

    this.isReconnecting = true;
    this.reconnectCount++;
    logger.info('AvatarPlayer', 'Attempting reconnection...');

    const reconnectStart = performance.now();
    trackEvent('rtc_reconnect_start', 'info', { provider: this.providerName });

    try {
      // Disconnect first if connected
      if (this._isConnected) {
        await this.disconnect();
      }

      // Wait a short moment before reconnecting
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Reconnect
      await this.connect(this.lastConnectionConfig);
      logger.info('AvatarPlayer', 'Reconnection successful');

      trackEvent('rtc_reconnect_success', 'info', {
        provider: this.providerName,
        duration: Math.round(performance.now() - reconnectStart),
      });
    } catch (error) {
      logger.error('AvatarPlayer', 'Reconnection failed:', error);
      trackEvent('rtc_reconnect_failed', 'error', {
        provider: this.providerName,
        reason: error instanceof Error ? error.message : String(error),
      });
      throw error;
    } finally {
      this.isReconnecting = false;
    }
  }

  /**
   * Create internal renderer adapter that bridges AnimationHandler to AvatarView.
   * @internal
   */
  private createRendererAdapter() {
    const avatarView = this.avatarView;

    return {
      renderFromProtobuf(protobufData: ArrayBuffer): void {
        avatarView.renderFromProtobuf(protobufData);
      },

      renderFrame(frame: unknown, startIdle?: boolean): void {
        const view = avatarView as any;
        if (startIdle) {
          view.renderFlame(undefined, true);
        } else if (frame) {
          view.renderFlame(frame);
        }
      },

      async generateTransitionFromProtobuf(
        protobufData: ArrayBuffer,
        frameCount: number,
      ): Promise<unknown[]> {
        return avatarView.generateTransitionFromProtobuf(protobufData, frameCount);
      },

      isReady(): boolean {
        return true;
      },
    };
  }

  /**
   * Setup animation track callbacks.
   * @internal
   */
  private async setupAnimationCallbacks(): Promise<void> {
    const callbacks: AnimationTrackCallbacks = {
      onAnimationData: (protobufData, metadata) => {
        // Reset tracking only once per session to avoid duplicate start-packet resets.
        if (!this.hasActiveAnimationSession) {
          if (!this.isTrackingPrimed) {
            this.animationHandler.resetTracking();
          }

          this.hasActiveAnimationSession = true;
          this.isTrackingPrimed = false;

          if (!metadata.isStart) {
            logger.info(
              'AvatarPlayer',
              `Session start inferred from frame seq=${metadata.frameSeq ?? 'n/a'}`,
            );
          }
        } else if (metadata.isStart) {
          logger.info(
            'AvatarPlayer',
            `Ignoring duplicate session start frame seq=${metadata.frameSeq ?? 'n/a'}`,
          );
        }

        this.animationHandler.handleAnimationData(
          protobufData,
          metadata.frameSeq,
          metadata.isRecovered,
        );
      },
      onTransition: (protobufData, transitionFrameCount) => {
        this.conversationCount++;
        this.animationHandler.handleTransitionData(
          protobufData,
          transitionFrameCount,
        );
      },
      onTransitionEnd: (protobufData, transitionFrameCount) => {
        this.animationHandler.handleTransitionToIdle(
          protobufData,
          transitionFrameCount,
        );
      },
      onSessionStart: () => {
        // Session started - can be used for UI feedback
      },
      onSessionEnd: () => {
        // Session ended - can be used for UI feedback
      },
      onIdleStart: () => {
        // Idle packets can race with start-transition packets; avoid rendering idle mid-transition.
        if (this.animationHandler.isInTransition()) {
          // Keep session bookkeeping in sync even if visual idle is deferred.
          this.hasActiveAnimationSession = false;
          this.isTrackingPrimed = false;
          logger.info(
            'AvatarPlayer',
            'Deferring idleStart while transition is active',
          );
          return;
        }

        this.hasActiveAnimationSession = false;
        this.animationHandler.resetTracking();
        this.isTrackingPrimed = true;
        this.animationHandler.startIdle();
      },
      onStreamStats: (stats) => {
        // Log packet loss/recovery stats
        if (
          stats.framesLost > 0 ||
          stats.framesRecovered > 0 ||
          stats.framesDropped > 0
        ) {
          logger.warn(
            'AvatarPlayer',
            `Stream stats: lost=${stats.framesLost}, recovered=${stats.framesRecovered}, dropped=${stats.framesDropped}, fps=${stats.framesPerSec}`,
          );
        }
      },
    };

    await this.provider.subscribeAnimationTrack(callbacks);
  }

  /**
   * Setup provider event handlers.
   * @internal
   */
  private setupProviderEvents(): void {
    this.provider.on('connected', () => {
      this._isConnected = true;
    });

    this.provider.on('disconnected', () => {
      this._isConnected = false;
      this.hasActiveAnimationSession = false;
      this.isTrackingPrimed = false;
      // Return to idle animation when disconnected
      this.animationHandler.startIdle();
    });

    this.provider.on('error', (error: unknown) => {
      trackEvent('rtc_error', 'error', {
        provider: this.providerName,
        description: error instanceof Error ? error.message : String(error),
      });
    });
  }
}
